import getSymbolFromCurrency from "currency-symbol-map";
import React, { useMemo } from "react";
import { useImmer } from "use-immer";

import {
  IgAutocomplete,
  IgButton,
  IgHeaderWTooltip,
  IgPaper,
  IgSwitch,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { ShopifyCurrencyEnum } from "@intelligems/ig-types";
import { Grid, MenuItem, useTheme } from "@mui/material";

import { usePatchConfigurations } from "../../api/configurations/patch-configurations";
import { useConfiguration } from "../../api/configurations/use-configuration";

const mockValue = "150.00";

const options = [
  {
    key: "removeTrailingZeros",
    label: "Remove Trailing Zeros",
    tooltip: "Remove insignificant trailing zeros",
    inputType: "switch",
  },
  {
    key: "symbol",
    label: "Currency Symbol",
    tooltip: "The currency symbol used for your prices",
    inputType: "select",
  },
  // {
  //   key: "prefix",
  //   label: "Prefix",
  //   tooltip: "A value to be inserted before your prices",
  //   inputType: "textfield",
  // },
  {
    key: "suffix",
    label: "Suffix",
    tooltip: "A value to be inserted after your prices",
    inputType: "textfield",
  },
];

export const CurrencySettings = () => {
  const mutateConfigurations = usePatchConfigurations();
  const currencyFormat = useConfiguration("currency_format");
  const theme = useTheme();
  const [modifiedCurrencySetting, setModifiedCurrencySetting] = useImmer<any>({
    suffix: currencyFormat?.value.suffix || "",
    symbol: currencyFormat?.value.symbol || "$",
    removeTrailingZeros:
      currencyFormat?.value.removeTrailingZeros !== undefined
        ? currencyFormat.value.removeTrailingZeros
        : true,
  });

  const formattedOutput = useMemo(() => {
    return `${modifiedCurrencySetting.symbol}${
      modifiedCurrencySetting.removeTrailingZeros
        ? mockValue.replace(/\D00(?=\D*$)/, "")
        : mockValue
    }${modifiedCurrencySetting.suffix}`;
  }, [mockValue, modifiedCurrencySetting, currencyFormat?.value]);

  const onCurrencyFormatSave = () => {
    mutateConfigurations.mutate([
      {
        ...currencyFormat,
        value: {
          ...currencyFormat?.value,
          removeTrailingZeros: modifiedCurrencySetting.removeTrailingZeros,
          symbol: modifiedCurrencySetting.symbol,
          suffix: modifiedCurrencySetting.suffix,
        },
      },
    ]);
  };
  return (
    <Grid item>
      <IgPaper>
        <Grid container direction={"column"} rowSpacing={3}>
          <Grid item>
            <IgTypography variant="h3" fontWeight={"fontWeightMedium"}>
              Advanced Currency Settings
            </IgTypography>
          </Grid>
          <Grid item>
            <IgTypography color={theme.palette.text.secondary}>
              These settings allow you to customize the look and formatting of
              your prices during a test.
            </IgTypography>
          </Grid>
          <Grid item container direction={"row"}>
            <Grid item container direction={"column"} xs={6} rowSpacing={1}>
              <Grid item>
                <IgTypography igStyle={"semibold"}>Options</IgTypography>
              </Grid>
              {options.map((option) => (
                <Grid
                  item
                  container
                  direction={"row"}
                  alignItems={"center"}
                  key={option.key}
                >
                  <Grid item>
                    <IgHeaderWTooltip
                      variant={"body1"}
                      sectionTitle={option.label}
                      tooltipMessage={option.tooltip}
                    />
                  </Grid>
                  <Grid item>
                    {option.inputType === "switch" ? (
                      <IgSwitch
                        checked={modifiedCurrencySetting[option.key]}
                        color="primary"
                        onChange={(e) => {
                          setModifiedCurrencySetting((draft) => {
                            draft[option.key] = e.target.checked;
                            return draft;
                          });
                        }}
                      />
                    ) : option.inputType === "select" ? (
                      <IgAutocomplete
                        value={modifiedCurrencySetting[option.key]}
                        disableClearable={true}
                        sx={{
                          "& .MuiPaper-root": { maxHeight: "300px" },
                          width: "200px",
                        }}
                        options={Object.keys(ShopifyCurrencyEnum)}
                        onChange={(e, newValue) => {
                          setModifiedCurrencySetting((draft) => {
                            draft[option.key] = getSymbolFromCurrency(newValue);

                            return draft;
                          });
                        }}
                        getOptionLabel={(option) => {
                          return `${option}`;
                        }}
                        renderOption={(props, option) => (
                          <MenuItem {...props} value={option} key={option}>
                            {ShopifyCurrencyEnum[option]} ({option}{" "}
                            {getSymbolFromCurrency(option)})
                          </MenuItem>
                        )}
                        renderInput={(params) => <IgTextField {...params} />}
                      />
                    ) : option.inputType === "textfield" ? (
                      <IgTextField
                        value={modifiedCurrencySetting[option.key]}
                        onChange={(e) => {
                          setModifiedCurrencySetting((draft) => {
                            draft[option.key] = e.target.value;
                            return draft;
                          });
                        }}
                      />
                    ) : null}
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid item container direction={"column"} xs={6} rowSpacing={1}>
              <Grid item>
                <IgTypography igStyle={"semibold"}>Example Output</IgTypography>
              </Grid>
              <Grid item>
                <IgTypography>{formattedOutput}</IgTypography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item mt={2}>
            <IgButton
              loading={mutateConfigurations.isPending}
              onClick={onCurrencyFormatSave}
              variant={"contained"}
            >
              Save
            </IgButton>
          </Grid>
        </Grid>
      </IgPaper>
    </Grid>
  );
};
